import type { EdgeId, NodeId } from '@ipfabric/utils';
import { createCurtain } from 'components/Graph/GraphApp/helpers/curtain';
import type {
  GraphRendererPlugin,
  GraphRendererPluginInterface,
} from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import {
  ZINDEX_EDGE,
  ZINDEX_EDGE_HIGHLIGHTED,
  ZINDEX_HIGHLIGHT_CURTAIN,
  ZINDEX_NODE,
  ZINDEX_NODE_HIGHLIGHTED,
} from 'components/Graph/GraphApp/zIndexes';
import type PIXI from 'pixi.js';
import {
  ANIMATION_BACKWARD,
  type AnimationDirection,
  createBouncingAnimationController,
} from 'utils/pixi/animation/BouncingAnimationController';
import { easeOutCubic } from 'utils/pixi/animation/easing';
import { suspendAnimateOnLowFps } from 'utils/pixi/animation/suspendAnimateOnLowFps';

const HIGHLIGHT_ALPHA = 0.85;
export const GRAPH_PART_HIGHLIGHT_ANIMATION_TIME = 200;

const createHighlightCurtain = (
  pluginInterface: GraphRendererPluginInterface,
): [curtain: PIXI.Graphics, drawCurtain: () => void] => {
  const [curtain, drawCurtain] = createCurtain(pluginInterface);
  curtain.name = 'highlight-curtain';
  curtain.zIndex = ZINDEX_HIGHLIGHT_CURTAIN;
  curtain.visible = false;
  pluginInterface.graphLayer.addChild(curtain);
  return [curtain, drawCurtain];
};

export const highlightGraphPart: GraphRendererPlugin = (pluginInterface) => {
  const [curtain, drawCurtain] = createHighlightCurtain(pluginInterface);

  const unhighlightAll = () => {
    // biome-ignore lint/complexity/noForEach: called on Map
    pluginInterface.edgeDisplayObjects.forEach((edgeObject) => {
      edgeObject.zIndex = ZINDEX_EDGE;
    });
    // biome-ignore lint/complexity/noForEach: called on Map
    pluginInterface.nodeDisplayObjects.forEach((nodeObject) => {
      nodeObject.zIndex = ZINDEX_NODE;
    });
  };

  const highlightEdge = (edgeId: EdgeId) => {
    const edgeObject = pluginInterface.edgeDisplayObjects.get(edgeId);
    if (edgeObject) {
      edgeObject.zIndex = ZINDEX_EDGE_HIGHLIGHTED;
    }
  };

  const highlightNode = (nodeId: NodeId) => {
    const nodeObject = pluginInterface.nodeDisplayObjects.get(nodeId);
    if (nodeObject) {
      nodeObject.zIndex = ZINDEX_NODE_HIGHLIGHTED;
    }
  };

  const renderAnimationFrame = (
    phase: number,
    direction: AnimationDirection,
  ) => {
    if (direction === ANIMATION_BACKWARD && phase === 0) {
      unhighlightAll();
      curtain.visible = false;
    } else {
      curtain.visible = true;
    }
    curtain.alpha = phase * HIGHLIGHT_ALPHA;
  };
  const suspendableAnimate = suspendAnimateOnLowFps(
    pluginInterface.animate,
    pluginInterface.requestRender,
  );
  const animationController = createBouncingAnimationController(
    suspendableAnimate,
    GRAPH_PART_HIGHLIGHT_ANIMATION_TIME,
    renderAnimationFrame,
    easeOutCubic,
  );

  pluginInterface.events.addListener(
    'highlightGraphPart',
    ({ nodes, edges, clear }) => {
      if (clear) {
        unhighlightAll();
      }
      drawCurtain();
      nodes?.forEach(highlightNode);
      edges?.forEach(highlightEdge);
      animationController.forward();
    },
  );

  pluginInterface.events.addListener('unhighlightGraphPart', () => {
    animationController.backward();
  });
};
